import { useState, useEffect, useRef, useMemo } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MOCK_VESSELS, MOCK_VOYAGES, OFFSHORE_ASSETS, VESSEL_STATUS_COLORS, MockVoyage, resolveClientKey, getClientVessels, getClientVoyages, getClientAssets } from "@/data/portalMockData";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import { Ship, Anchor, Calendar, Users as UsersIcon, Search, Download, ChevronDown, ChevronUp, Clock, MapPin, Wind, Gauge, Waves, Thermometer, Eye } from "lucide-react";
import { toast } from "sonner";

const statusColors: Record<string, string> = {
  in_progress: "bg-blue-100 text-blue-700",
  confirmed: "bg-green-100 text-green-700",
  scheduled: "bg-amber-100 text-amber-700",
  completed: "bg-gray-100 text-gray-600",
  cancelled: "bg-red-100 text-red-700",
};

const ASSET_TYPE_COLORS: Record<string, string> = {
  FPSO: "#E74C3C",
  Drillship: "#3498DB",
  Terminal: "#FF9F1C",
  "Semi-Sub": "#F39C12",
};

// ── Asset Map Component ──────────────────────────────────────
const AssetMap = ({ clientFilter }: { clientFilter: string }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapReady, setMapReady] = useState(false);

  const filteredAssets = useMemo(() => {
    if (!clientFilter) return OFFSHORE_ASSETS;
    return getClientAssets(clientFilter);
  }, [clientFilter]);

  const filteredVessels = useMemo(() => {
    if (!clientFilter) return MOCK_VESSELS;
    return getClientVessels(clientFilter);
  }, [clientFilter]);

  useEffect(() => {
    let map: any;
    const loadMap = async () => {
      const L = (await import("leaflet")).default;
      await import("leaflet/dist/leaflet.css");
      if (!mapRef.current) return;

      map = L.map(mapRef.current, {
        center: [6, -40],
        zoom: 3,
        zoomControl: false,
        attributionControl: false,
      });

      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);
      L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}", {
        maxZoom: 19,
      }).addTo(map);
      L.control.zoom({ position: "bottomright" }).addTo(map);

      filteredAssets.forEach((asset) => {
        const color = ASSET_TYPE_COLORS[asset.type] || "#fff";
        const size = asset.type === "Terminal" ? 18 : 16;
        const opStatus = asset.operatorStatus || "operated";
        let shape: string;

        if (asset.type === "Terminal") {
          const opacity = opStatus === "future" ? "0.4" : opStatus === "non-operated" ? "0.6" : "1";
          shape = `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" style="filter:drop-shadow(0 0 4px ${color}80);opacity:${opacity};"><path d="M12 2L12 10M12 10C9.79 10 8 11.79 8 14M12 10C14.21 10 16 11.79 16 14M8 14H5L7 22H17L19 14H16M8 14H16M6 6H18" stroke="${color}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
        } else if (opStatus === "future") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:transparent;border:2px dashed ${color};box-shadow:0 0 10px ${color}40;${tr}"></div>`;
        } else if (opStatus === "non-operated") {
          const br = asset.type === "FPSO" ? "0" : "50%";
          const tr = asset.type === "FPSO" ? "transform:rotate(45deg);" : "";
          shape = `<div style="width:${size}px;height:${size}px;border-radius:${br};background:${color}55;border:2px dashed #fff;box-shadow:0 0 10px ${color}60;${tr}"></div>`;
        } else {
          shape = asset.type === "FPSO"
            ? `<div style="width:${size}px;height:${size}px;background:${color};border:2px solid #fff;box-shadow:0 0 12px ${color}80;transform:rotate(45deg);"></div>`
            : `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 12px ${color}80;"></div>`;
        }

        const icon = L.divIcon({
          className: "",
          html: shape,
          iconSize: [size, size],
          iconAnchor: [size / 2, size / 2],
        });

        const statusLabels: Record<string, string> = { operated: "🟢 Operated", "non-operated": "🟡 Non-Operated Interest", future: "🔵 Under Construction" };
        const statusLine = opStatus !== "operated" ? `<div style="font-size:11px;font-weight:600;margin:4px 0;color:${opStatus === "non-operated" ? "#F1C40F" : "#3498DB"};">${statusLabels[opStatus]}</div>` : "";
        const operatorLine = asset.operatedBy ? `<div style="font-size:11px;color:#aaa;">Operator: ${asset.operatedBy} · ${asset.equityPct}% interest</div>` : "";
        const capacityLine = asset.capacityBpd ? `<div style="font-size:11px;color:#aaa;">Capacity: ${(asset.capacityBpd / 1000).toFixed(0)}K bpd</div>` : "";

        L.marker([asset.lat, asset.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:200px;">
              <div style="font-weight:700;font-size:14px;margin-bottom:2px;">${asset.name}</div>
              <div style="font-size:11px;color:#888;margin-bottom:6px;">${asset.type} · ${asset.region}</div>
              ${statusLine}
              ${asset.field ? `<div style="font-size:11px;color:#aaa;">Field: ${asset.field}</div>` : ""}
              ${operatorLine}${capacityLine}
              <div style="font-size:10px;color:#aaa;margin-top:4px;">${Math.abs(asset.lat).toFixed(4)}° ${asset.lat >= 0 ? "N" : "S"}, ${Math.abs(asset.lng).toFixed(4)}° ${asset.lng >= 0 ? "E" : "W"}</div>
            </div>
          `);
      });

      filteredVessels.forEach((v) => {
        const statusInfo = VESSEL_STATUS_COLORS[v.status] || VESSEL_STATUS_COLORS.available;
        const color = statusInfo.bg;
        const isMoving = v.status === "in_transit";
        const pulseRing = isMoving ? `<div style="position:absolute;top:-4px;left:-4px;width:20px;height:20px;border-radius:50%;border:2px solid ${color};opacity:0.4;animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>` : "";
        const icon = L.divIcon({
          className: "",
          html: `<div style="position:relative;width:12px;height:12px;">${pulseRing}<div style="position:absolute;top:0;left:0;width:12px;height:12px;border-radius:50%;background:${color};border:2px solid #fff;box-shadow:0 0 8px ${color}80;"></div></div>`,
          iconSize: [12, 12],
          iconAnchor: [6, 6],
        });
        const statusLabel = statusInfo.label;
        const alongsideInfo = v.status === "alongside" ? `<div style="font-size:11px;color:#28A745;font-weight:600;margin:4px 0;">⚓ Alongside ${v.destination || ""}</div>` : "";
        const transitInfo = v.status === "in_transit" ? `<div style="font-size:11px;color:#0066CC;font-weight:600;margin:4px 0;">🚢 En route to ${v.destination || ""}</div>` : "";
        const shoreInfo = v.status === "available" ? `<div style="font-size:11px;color:#17A2B8;font-weight:600;margin:4px 0;">🏠 At Shore Base</div>` : "";
        const paxLine = v.paxOnBoard > 0
          ? `<div style="font-size:11px;margin:2px 0;">👥 <b>${v.paxOnBoard}</b> passengers on board</div>`
          : `<div style="font-size:11px;color:#888;margin:2px 0;">No passengers on board</div>`;
        L.marker([v.lat, v.lng], { icon })
          .addTo(map)
          .bindPopup(`
            <div style="font-family:'DM Sans',sans-serif;min-width:240px;max-width:300px;">
              <div style="font-weight:700;font-size:15px;margin-bottom:2px;">${v.name}</div>
              <div style="font-size:11px;color:#666;margin-bottom:6px;">${v.type}</div>
              <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px;">
                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};"></span>
                <span style="font-size:12px;font-weight:600;">${statusLabel}</span>
              </div>
              ${alongsideInfo}${transitInfo}${shoreInfo}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:11px;margin:2px 0;">🏴 <b>Flag:</b> ${v.flag}</div>
              <div style="font-size:11px;margin:2px 0;">⚡ <b>Speed:</b> ${v.currentSpeed > 0 ? v.currentSpeed + " kts" : "0 kts (stationary)"}</div>
              <div style="font-size:11px;margin:2px 0;">👨‍✈️ <b>Captain:</b> ${v.captain}</div>
              <div style="font-size:11px;margin:2px 0;">🔧 <b>Chief Engineer:</b> ${v.chiefEngineer}</div>
              ${paxLine}
              <hr style="border:none;border-top:1px solid #eee;margin:6px 0;"/>
              <div style="font-size:10px;color:#aaa;">IMO: ${v.imoNumber} · MMSI: ${v.mmsi}</div>
              <div style="font-size:10px;color:#aaa;">${v.lat.toFixed(4)}° ${v.lat >= 0 ? "N" : "S"}, ${Math.abs(v.lng).toFixed(4)}° ${v.lng >= 0 ? "E" : "W"}</div>
            </div>
          `, { maxWidth: 320 });
      });

      const allPoints = [
        ...filteredAssets.map(a => [a.lat, a.lng] as [number, number]),
        ...filteredVessels.map(v => [v.lat, v.lng] as [number, number]),
      ];
      if (allPoints.length > 0) {
        const bounds = L.latLngBounds(allPoints);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 8 });
      }

      setMapReady(true);
    };
    loadMap();
    return () => { map?.remove(); };
  }, [filteredAssets, filteredVessels]);

  return (
    <div className="relative rounded-xl overflow-hidden border border-[hsl(220,15%,90%)]" style={{ height: 420 }}>
      {!mapReady && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-[hsl(220,20%,97%)]">
          <div className="w-6 h-6 border-2 border-[hsl(211,100%,40%)]/30 border-t-[hsl(211,100%,40%)] rounded-full animate-spin" />
        </div>
      )}
      <div ref={mapRef} className="w-full h-full" />
      <div className="absolute bottom-3 left-3 z-[1000] bg-black/70 backdrop-blur-sm rounded-lg px-3 py-2 flex flex-wrap gap-3">
        {Object.entries(ASSET_TYPE_COLORS).map(([type, color]) => (
          <div key={type} className="flex items-center gap-1.5">
            <div className="w-3 h-3" style={{
              background: color,
              borderRadius: type === "Drillship" ? "50%" : type === "FPSO" ? "0" : "2px",
              transform: type === "FPSO" ? "rotate(45deg) scale(0.7)" : "none",
            }} />
            <span className="text-[10px] text-white/80">{type}</span>
          </div>
        ))}
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-[#28A745]" />
          <span className="text-[10px] text-white/80">Vessel</span>
        </div>
      </div>
    </div>
  );
};

const VoyageOperations = () => {
  const [expandedVoyage, setExpandedVoyage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterVessel, setFilterVessel] = useState("all");
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);

  const clientVessels = clientKey ? getClientVessels(clientKey) : MOCK_VESSELS;
  const allVoyages = clientKey ? getClientVoyages(clientKey) : MOCK_VOYAGES;
  const activeVoyages = allVoyages.filter(v => ["in_progress", "confirmed", "scheduled"].includes(v.status));
  const historyVoyages = allVoyages.filter(v => v.status === "completed");

  const filteredHistory = historyVoyages.filter(v => {
    const matchesSearch = v.voyageId.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.vessel.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.destination.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesVessel = filterVessel === "all" || v.vessel === filterVessel;
    return matchesSearch && matchesVessel;
  });

  const handleSubmitRequest = (e: React.FormEvent) => {
    e.preventDefault();
    toast.success("Voyage Request Submitted", { description: "Your request has been submitted and is under review." });
  };

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Operations</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Voyage Operations</h1>
      </div>

      {/* ── Interactive Asset Map ── */}
      <Card className="mb-6">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[hsl(211,100%,40%)]" />
              <CardTitle className="text-base">Offshore Assets & Vessel Tracking</CardTitle>
            </div>
            <span className="text-[10px] text-[hsl(215,25%,17%)]/40">
              {(clientKey ? getClientAssets(clientKey) : OFFSHORE_ASSETS).length} assets ·{" "}
              {clientVessels.length} vessels
            </span>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <AssetMap clientFilter={clientKey} />
        </CardContent>
      </Card>

      <Tabs defaultValue="active" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="request">Request</TabsTrigger>
          <TabsTrigger value="active">Active & Upcoming</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        {/* Voyage Request Form */}
        <TabsContent value="request">
          <div className="portal-card p-6 max-w-2xl">
            <h2 className="text-lg font-bold text-[hsl(215,25%,17%)] mb-4">New Voyage Request</h2>
            <form onSubmit={handleSubmitRequest} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Vessel</label>
                  <Select><SelectTrigger><SelectValue placeholder="Select vessel" /></SelectTrigger>
                    <SelectContent>{clientVessels.map(v => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Priority</label>
                  <Select defaultValue="normal"><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="normal">Normal</SelectItem><SelectItem value="urgent">Urgent</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Departure Point</label><Input placeholder="e.g. Shore Base Georgetown" /></div>
                <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Destination</label><Input placeholder="e.g. FPSO Liza Unity" /></div>
                <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Date & Time</label><Input type="datetime-local" /></div>
                <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Passenger Count</label><Input type="number" placeholder="0" /></div>
              </div>
              <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Special Requirements</label><Input placeholder="Any special requirements..." /></div>
              <Button type="submit" className="bg-[hsl(211,100%,40%)] hover:bg-[hsl(211,100%,35%)] text-white">Submit Request</Button>
            </form>
          </div>
        </TabsContent>

        {/* Active Voyages */}
        <TabsContent value="active">
          <div className="space-y-3">
            {activeVoyages.map(voy => (
              <div key={voy.id} className="portal-card overflow-hidden">
                <div className="p-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedVoyage(expandedVoyage === voy.id ? null : voy.id)}>
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${voy.status === "in_progress" ? "bg-[#0066CC] animate-pulse" : voy.status === "confirmed" ? "bg-[#28A745]" : "bg-[#FFC107]"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm text-[hsl(215,25%,17%)]">{voy.voyageId}</span>
                      <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${statusColors[voy.status]}`}>{voy.status.replace("_", " ")}</span>
                    </div>
                    <p className="text-xs text-[hsl(215,12%,50%)]">{voy.vessel} · {voy.origin} → {voy.destination}</p>
                  </div>
                  <div className="hidden md:flex items-center gap-6 text-center">
                    <div><p className="text-sm font-bold text-[hsl(215,25%,17%)]">{voy.pax}</p><p className="text-[9px] text-[hsl(215,12%,50%)]">PAX</p></div>
                    <div><p className="text-sm font-bold text-[hsl(215,25%,17%)]">{new Date(voy.departureTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p><p className="text-[9px] text-[hsl(215,12%,50%)]">Depart</p></div>
                    <div><p className="text-sm font-bold text-[hsl(215,25%,17%)]">{new Date(voy.eta).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p><p className="text-[9px] text-[hsl(215,12%,50%)]">ETA</p></div>
                  </div>
                  {expandedVoyage === voy.id ? <ChevronUp className="w-4 h-4 text-[hsl(215,12%,50%)]" /> : <ChevronDown className="w-4 h-4 text-[hsl(215,12%,50%)]" />}
                </div>
                {expandedVoyage === voy.id && (
                  <div className="px-4 pb-4 pt-0 border-t border-[hsl(220,15%,90%)] bg-[hsl(220,20%,97%)]">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-3 text-xs">
                      <div><span className="text-[hsl(215,12%,50%)]">Master:</span> <span className="font-medium">{voy.master}</span></div>
                      <div><span className="text-[hsl(215,12%,50%)]">Duration:</span> <span className="font-medium">{voy.duration}</span></div>
                      <div><span className="text-[hsl(215,12%,50%)]">Fuel Est.:</span> <span className="font-medium">{voy.fuelMT} MT</span></div>
                      <div className="flex items-center gap-1"><Gauge className="w-3 h-3 text-[hsl(211,100%,40%)]" /><span className="text-[hsl(215,12%,50%)]">Avg Speed:</span> <span className="font-medium">{voy.avgSpeedKts} kts</span></div>
                      {voy.cargo && <div className="col-span-2"><span className="text-[hsl(215,12%,50%)]">Cargo:</span> <span className="font-medium">{voy.cargoDesc} ({voy.cargoWeight} MT)</span></div>}
                    </div>
                    {/* Weather & Sea State Panel */}
                    <div className="mt-2 p-3 rounded-lg bg-white border border-[hsl(220,15%,92%)]">
                      <p className="text-[10px] font-bold tracking-[2px] uppercase text-[hsl(215,12%,50%)] mb-2">
                        {voy.status === "completed" ? "Recorded Conditions" : voy.status === "in_progress" ? "Current Conditions" : "Forecast Conditions"}
                      </p>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-xs">
                        <div className="flex items-center gap-1.5">
                          <Waves className="w-3.5 h-3.5 text-[hsl(211,100%,40%)]" />
                          <div><p className="text-[hsl(215,12%,50%)]">Sig. Wave Ht</p><p className="font-bold text-[hsl(215,25%,17%)]">{voy.weatherHs} m</p></div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Wind className="w-3.5 h-3.5 text-[hsl(211,100%,40%)]" />
                          <div><p className="text-[hsl(215,12%,50%)]">Wind</p><p className="font-bold text-[hsl(215,25%,17%)]">{voy.weatherWind} kts</p></div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Anchor className="w-3.5 h-3.5 text-[hsl(211,100%,40%)]" />
                          <div><p className="text-[hsl(215,12%,50%)]">Sea State</p><p className="font-bold text-[hsl(215,25%,17%)]">{voy.seaState}</p></div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Thermometer className="w-3.5 h-3.5 text-[hsl(211,100%,40%)]" />
                          <div><p className="text-[hsl(215,12%,50%)]">Water Temp</p><p className="font-bold text-[hsl(215,25%,17%)]">{voy.waterTempC}°C</p></div>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5 text-[hsl(211,100%,40%)]" />
                          <div><p className="text-[hsl(215,12%,50%)]">Visibility</p><p className="font-bold text-[hsl(215,25%,17%)]">{voy.visibilityNM} NM</p></div>
                        </div>
                      </div>
                      <p className="text-[10px] text-[hsl(215,12%,50%)] mt-2">Conditions: <span className="font-medium text-[hsl(215,25%,17%)]">{voy.weatherCondition}</span></p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </TabsContent>

        {/* Voyage History */}
        <TabsContent value="history">
          <div className="portal-card">
            <div className="p-4 flex flex-wrap items-center gap-3 border-b border-[hsl(220,15%,90%)]">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(215,12%,50%)]" />
                <Input placeholder="Search voyages..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-[hsl(220,20%,97%)] border-[hsl(220,15%,90%)]" />
              </div>
              <Select value={filterVessel} onValueChange={setFilterVessel}>
                <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Vessels</SelectItem>
                  {clientVessels.map(v => <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => toast.success("Generating: Export CSV", { description: "Your file will download shortly." })} variant="outline" size="sm"><Download className="w-3.5 h-3.5 mr-1" /> Export CSV</Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b border-[hsl(220,15%,90%)] bg-[hsl(220,20%,97%)]">
                  {["Voyage ID", "Date", "Vessel", "Route", "PAX", "Avg Speed", "Sea State", "Wind", "Hs", "Score", "Status"].map(h => (
                    <th key={h} className="px-3 py-2.5 text-left font-medium text-[hsl(215,12%,50%)] whitespace-nowrap">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {filteredHistory.map(voy => (
                    <tr key={voy.id} className="border-b border-[hsl(220,15%,92%)] hover:bg-[hsl(220,20%,97%)] transition-colors">
                      <td className="px-3 py-2.5 font-medium text-[hsl(215,25%,17%)]">{voy.voyageId}</td>
                      <td className="px-3 py-2.5 text-[hsl(215,12%,50%)]">{new Date(voy.departureTime).toLocaleDateString()}</td>
                      <td className="px-3 py-2.5">{voy.vessel}</td>
                      <td className="px-3 py-2.5 text-[hsl(215,12%,50%)]">{voy.origin} → {voy.destination}</td>
                      <td className="px-3 py-2.5 text-center">{voy.pax}</td>
                      <td className="px-3 py-2.5 text-center font-medium">{voy.avgSpeedKts} kts</td>
                      <td className="px-3 py-2.5">{voy.seaState}</td>
                      <td className="px-3 py-2.5 text-center">{voy.weatherWind} kts</td>
                      <td className="px-3 py-2.5 text-center">{voy.weatherHs} m</td>
                      <td className="px-3 py-2.5 text-center">{voy.satScore > 0 ? `${voy.satScore} ⭐` : "—"}</td>
                      <td className="px-3 py-2.5"><span className={`text-[9px] font-bold px-2 py-0.5 rounded-full uppercase ${statusColors[voy.status]}`}>{voy.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default VoyageOperations;
